import React, {
  createContext,
  useContext,
  useState,
  // useEffect,
  useCallback,
} from "react";
import { authAPI, TokenService } from "../services/Api";
// import { useNavigate } from "react-router-dom";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  // const navigate = useNavigate();
  // Initialize user state directly from cookies (synchronous read)
  const [user, setUser] = useState(() => {
    const storedUser = TokenService.getUser();
    const token = TokenService.getToken();
    return token && storedUser ? storedUser : null;
  });

  // Loading is false since we read from cookies synchronously
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // useEffect(() => {
  //   if (user?.role?.id) {
  //     const roleId = Number(user.role.id);

  //     const redirectPath = roleId === 1 ? "/dashboard" : "/user/dashboard";

  //     navigate(redirectPath, { replace: true });
  //   }
  // }, [user, navigate]);

  // Check authentication on mount - COMMENTED AS REQUESTED
  // useEffect(() => {
  //   checkAuth();
  // }, []);

  // Check if user is authenticated (can be called manually if needed)
  const checkAuth = async () => {
    setLoading(true);

    try {
      const token = TokenService.getToken();
      const storedUser = TokenService.getUser();

      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }

      if (storedUser) {
        setUser(storedUser);
      }

      try {
        const response = await authAPI.getProfile();

        if (response.data?.success) {
          const profileData = response.data.data;
          const updatedUser = {
            ...storedUser,
            ...profileData,
          };
          setUser(updatedUser);
          TokenService.setUser(updatedUser);
        } else if (response.data?.success === false) {
          await performLogout();
        }
      } catch (apiError) {
        console.error("Profile fetch failed:", apiError);
        if (apiError.response?.status === 401) {
          await performLogout();
        }
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };
  const [loginInProgress, setLoginInProgress] = useState(false);

  // Login function
  const login = async (credentials) => {
    setError(null);
    setLoginInProgress(true);
    try {
      const response = await authAPI.login(credentials);

      if (response.data?.success) {
        const { token, ...userData } = response.data.data;
        setUser(userData);
        return {
          success: true,
          message: response.data.message,
          user: userData,
          token,
        };
      }

      // 🔑 Failure from API — clear any stale auth state + cookies
      TokenService.clearAll();
      setUser(null);
      setLoginInProgress(false);

      return {
        success: false,
        message: response.data?.message || "Login failed",
      };
    } catch (error) {
      console.error("Login error:", error);
      const errorMessage =
        error.response?.data?.message || "Login failed. Please try again.";

      // 🔑 Exception path — also clear stale auth state
      TokenService.clearAll();
      setUser(null);
      setError(errorMessage);
      setLoginInProgress(false);

      return { success: false, message: errorMessage };
    }
  };

  // Internal logout function
  const performLogout = async () => {
    TokenService.clearAll();
    setUser(null);
    setError(null);
  };

  // Logout function - calls API and clears state
  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error("Logout API error:", error);
    } finally {
      await performLogout();
    }
  }, []);

  // Refresh token function
  const refreshToken = async () => {
    try {
      const response = await authAPI.refreshToken();

      if (response.data?.success) {
        const newToken = response.data.data.token;
        TokenService.setToken(newToken);
        return { success: true };
      } else {
        performLogout();
      }

      return { success: false };
    } catch (error) {
      console.error("Token refresh error:", error);
      return { success: false };
    }
  };

  // Check if user has specific role(s)
  const hasRole = useCallback(
    (roles) => {
      if (!user || !user.role) return false;

      const userRoleSlug = user.role?.slug || user.role;

      if (Array.isArray(roles)) {
        return roles.some(
          (role) => role === userRoleSlug || role === user.role?.name,
        );
      }

      return userRoleSlug === roles || user.role?.name === roles;
    },
    [user],
  );

  // Check if user has specific permission
  const hasPermission = useCallback(
    (permission) => {
      if (!user || !user.permissions) return false;
      return user.permissions.includes(permission);
    },
    [user],
  );

  // Get user display name
  const getUserDisplayName = useCallback(() => {
    if (!user) return "";
    return user.name || user.email?.split("@")[0] || "User";
  }, [user]);
  const completeLoginFlow = () => {
    setLoginInProgress(false);
  };
  // Context value
  const value = {
    user,
    loading,
    error,
    login,
    logout,
    refreshToken,
    hasRole,
    hasPermission,
    getUserDisplayName,
    isAuthenticated: !!user,
    checkAuth,
    loginInProgress, // 🔥 ADD
    completeLoginFlow,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};

export default useAuth;
